import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {AchievementData} from "types/Achievement";
import {FormikHelpers} from "formik";
import AchievementForm from "../Form";
import {useOperator} from "../../../context/OperatorContext";

function MutateAchievement() {
  const {termFor} = useOperator();
  const {id} = useParams();
  const {data: achievement, isLoading} = useQuery(
    ['achievements', {id}],
    () => ngage.achievements.get(Number(id)),
    {enabled: !!id}
  );

  const store = useMutation((data: AchievementData) => ngage.achievements.store(data));
  const update = useMutation((data: AchievementData) => ngage.achievements.update(Number(id), data));

  const {notify} = useNotifications();
  const navigate = useNavigate();

  // Handle form submission
  async function handleSubmit(values: AchievementData, {setSubmitting}: FormikHelpers<AchievementData>) {
    id ? await handleUpdate(values) : await handleStore(values)
    await invalidate('achievements.all')

    setSubmitting(false)
    navigate('/achievements')
  }

  // Handle store mutation
  async function handleStore(values: AchievementData) {
    try {
      await store.mutateAsync(values)
      notify("success", 'Achievement Created', `${values.name} has been created`)
    } catch (error) {
      notify("error", 'Achievement Not Created', `${values.name} could not be stored`)
      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: AchievementData) {
    try {
      await update.mutateAsync(values)
      notify("success", `${termFor('Achievement')} Updated`, `${values.name} has been updated`)
      await invalidate(['achievements', {id}])
    } catch (error) {
      notify("error", `${termFor('Achievement')} Not Updated`, `${values.name} could not be updated`)
      throw error
    }
  }

  // Prepare breadcrumbs
  const newBreadcrumbs = {'App Content': '/', 'Interactive': '/', [termFor('Achievements')]: '/achievements', [`New ${termFor('Achievement')}`]: '/achievements/new'}
  const editBreadcrumbs = {
    'App Content': '/',
    'Interactive': '/',
    [termFor('Achievements')]: '/achievements',
    [`Edit ${termFor('Achievement')}`]: `/achievements/${id}/edit`
  }

  return (
    <Page
      title={`${id ? 'Edit' : 'New'} ${termFor('Achievement')}`}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      isLoading={isLoading}
    >
      <AchievementForm achievement={achievement} onSubmit={handleSubmit}/>
    </Page>
  );
}

export default MutateAchievement;